import type { User } from "@fitties/shared";
import { PageLoading } from "@shared/components/PageLoading";
import { Navigate } from "react-router-dom";
import { EventEntryErrorScreen } from "./EventEntryErrorScreen";
import { EventSwitchModal } from "./EventSwitchModal";

export type EventEntryContentProps = {
  reservedRedirect: string | null;
  validEventId: string;
  sessionReady: boolean;
  profileReady: boolean;
  status: string;
  user: User | null | undefined;
  userErrorMessage: string | null;
  needsSwitch: boolean;
  needsFirstApply: boolean;
  applyError: string | null;
  isApplyingFirstEvent: boolean;
  isApplyingSwitch: boolean;
  onRetryApply: () => void;
  onCancelSwitch: () => void;
  onConfirmSwitch: () => void;
};

export function EventEntryContent({
  reservedRedirect,
  validEventId,
  sessionReady,
  profileReady,
  status,
  user,
  userErrorMessage,
  needsSwitch,
  needsFirstApply,
  applyError,
  isApplyingFirstEvent,
  isApplyingSwitch,
  onRetryApply,
  onCancelSwitch,
  onConfirmSwitch,
}: EventEntryContentProps) {
  if (reservedRedirect) {
    return <Navigate replace to={reservedRedirect} />;
  }

  if (!sessionReady || (status === "authenticated" && !profileReady)) {
    return <PageLoading message="Loading event entry..." fullScreen />;
  }

  if (status !== "authenticated") {
    return <Navigate replace to="/auth" state={{ eventId: validEventId }} />;
  }

  if (!user) {
    return <Navigate replace to="/onboarding" />;
  }

  if (applyError) {
    return (
      <EventEntryErrorScreen
        applyError={applyError}
        userErrorMessage={userErrorMessage}
        showRetry={needsFirstApply}
        onRetry={onRetryApply}
      />
    );
  }

  if (isApplyingFirstEvent || isApplyingSwitch) {
    return <PageLoading message="Applying event..." fullScreen />;
  }

  if (needsSwitch) {
    return <EventSwitchModal isApplying={isApplyingSwitch} onCancel={onCancelSwitch} onConfirm={onConfirmSwitch} />;
  }

  return <Navigate replace to="/discover" />;
}
